import React from "react";
import {
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { DeltaChip } from "./DeltaChip";
import { splitRankingHalves } from "./rankingUtils.js";
import { useTranslation } from 'react-i18next'
const TONES = {
  indigo: {
    cardLight: "bg-gray-50",
    cardDark: "bg-gray-900/60",
    border: "border-indigo-200 dark:border-indigo-900/60",
    sideBar: "bg-indigo-500",
    iconLight: "bg-indigo-100 text-indigo-700",
    iconDark: "bg-indigo-900 text-indigo-100",
    title: "text-indigo-900 dark:text-indigo-100",
    badge:
      "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200",
  },
  emerald: {
    cardLight: "bg-gray-50",
    cardDark: "bg-gray-900/60",
    border: "border-emerald-200 dark:border-emerald-900/60",
    sideBar: "bg-emerald-500",
    iconLight: "bg-emerald-100 text-emerald-700",
    iconDark: "bg-emerald-900 text-emerald-100",
    title: "text-emerald-900 dark:text-emerald-100",
    badge:
      "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200",
  },
  amber: {
    cardLight: "bg-gray-50",
    cardDark: "bg-gray-900/60",
    border: "border-amber-200 dark:border-amber-900/60",
    sideBar: "bg-amber-500",
    iconLight: "bg-amber-100 text-amber-700",
    iconDark: "bg-amber-900 text-amber-100",
    title: "text-amber-900 dark:text-amber-100",
    badge:
      "bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200",
  },
  rose: {
    cardLight: "bg-gray-50",
    cardDark: "bg-gray-900/60",
    border: "border-rose-200 dark:border-rose-900/60",
    sideBar: "bg-rose-500",
    iconLight: "bg-rose-100 text-rose-700",
    iconDark: "bg-rose-900 text-rose-100",
    title: "text-rose-900 dark:text-rose-100",
    badge:
      "bg-rose-50 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200",
  },
  violet: {
    cardLight: "bg-gray-50",
    cardDark: "bg-gray-900/60",
    border: "border-violet-200 dark:border-violet-900/60",
    sideBar: "bg-violet-500",
    iconLight: "bg-violet-100 text-violet-700",
    iconDark: "bg-violet-900 text-violet-100",
    title: "text-violet-900 dark:text-violet-100",
    badge:
      "bg-violet-50 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200",
  },
  slate: {
    cardLight: "bg-gray-50",
    cardDark: "bg-gray-900/60",
    border: "border-slate-200 dark:border-slate-800/60",
    sideBar: "bg-slate-500",
    iconLight: "bg-slate-100 text-slate-700",
    iconDark: "bg-slate-800 text-slate-100",
    title: "text-slate-900 dark:text-slate-100",
    badge:
      "bg-slate-50 text-slate-700 dark:bg-slate-800/40 dark:text-slate-200",
  },
};

const MetricRankingCard = ({
  title,
  subtitle,
  icon: Icon,
  players,
  isDarkMode,
  getValue, // (player) => number | null
  formatValue, // (value: number) => string
  min = null,
  max = null,
  thresholds = null, // { low, high }
  renderExtra, // (player) => ReactNode
  tone = "slate",
  direction = "higherIsWorse",
  // Delta
  getDelta, // (player) => number | null
  deltaIsPercent = false,
  deltaUnit = "",
  deltaDecimals,
  deltaLabel = "vs last session",
  collapsed: collapsedProp,
  onCollapseChange,
  onPlayerClick,
  getBarStyle,
  hideZoneBackgrounds = false,
  referenceLines = null,
  invertDeltaColors = false,
  visualVariant = "default",
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const { t } = useTranslation() 
  // mode contrôlé / non contrôlé
  const [internalCollapsed, setInternalCollapsed] = React.useState(false);
  const collapsed = typeof collapsedProp === "boolean" ? collapsedProp : internalCollapsed;
  const setCollapsed = onCollapseChange || setInternalCollapsed;
  const palette = TONES[tone] ?? TONES.slate;
  const isTrainabilityVariant = visualVariant === "trainability";

  const trainabilityTextureStyle = isTrainabilityVariant
    ? {
        backgroundImage: isDarkMode
          ? "radial-gradient(circle at 16% 10%, rgba(255,255,255,0.12), transparent 44%), radial-gradient(circle at 84% 84%, rgba(148,163,184,0.18), transparent 42%), repeating-linear-gradient(125deg, rgba(148,163,184,0.08) 0px, rgba(148,163,184,0.08) 1px, transparent 1px, transparent 8px)"
          : "radial-gradient(circle at 12% 8%, rgba(255,255,255,0.96), transparent 48%), radial-gradient(circle at 86% 86%, rgba(148,163,184,0.24), transparent 44%), repeating-linear-gradient(125deg, rgba(148,163,184,0.1) 0px, rgba(148,163,184,0.1) 1px, transparent 1px, transparent 7px)",
      }
    : null;

  const cardClass = `relative overflow-hidden rounded-2xl px-4 py-4 shadow-sm border ${
    isTrainabilityVariant
      ? isDarkMode
        ? "border-slate-500/45 bg-slate-900/72"
        : "border-slate-300/90 bg-slate-100/85"
      : `${palette.border} ${isDarkMode ? palette.cardDark : palette.cardLight}`
  }`;

  const emptyCardClass = `relative overflow-hidden rounded-2xl px-4 py-3 shadow-sm border ${
    isTrainabilityVariant
      ? isDarkMode
        ? "border-slate-500/45 bg-slate-900/72"
        : "border-slate-300/90 bg-slate-100/85"
      : `${palette.border} ${isDarkMode ? palette.cardDark : palette.cardLight}`
  }`;

  const accentBarClass = isTrainabilityVariant
    ? isDarkMode
      ? "bg-gradient-to-b from-slate-200/80 via-slate-300/45 to-slate-500/45"
      : "bg-gradient-to-b from-white via-slate-300 to-slate-500/75"
    : palette.sideBar;

  const iconClass = isTrainabilityVariant
    ? isDarkMode
      ? "bg-slate-700/70 text-slate-100 ring-1 ring-white/10"
      : "bg-white/75 text-slate-700 ring-1 ring-slate-300/75 shadow-sm"
    : isDarkMode
      ? palette.iconDark
      : palette.iconLight;

  const titleClass = isTrainabilityVariant
    ? isDarkMode
      ? "text-slate-100"
      : "text-slate-800"
    : palette.title;

  const badgeClass = isTrainabilityVariant
    ? isDarkMode
      ? "bg-slate-700/60 text-slate-100"
      : "bg-white/80 text-slate-700 ring-1 ring-slate-300/70"
    : palette.badge;

  const entries = React.useMemo(() => {
    return (players || [])
      .map((p) => {
        const value = getValue(p);
        return Number.isFinite(value) ? { player: p, value } : null;
      })
      .filter(Boolean);
  }, [players, getValue]);

  if (!entries.length) {
    return (
      <div className={emptyCardClass}>
        {isTrainabilityVariant && (
          <>
            <div className="pointer-events-none absolute inset-0" style={trainabilityTextureStyle} />
            <div
              className={`pointer-events-none absolute inset-x-6 top-0 h-px ${
                isDarkMode
                  ? "bg-gradient-to-r from-transparent via-white/35 to-transparent"
                  : "bg-gradient-to-r from-transparent via-slate-400/45 to-transparent"
              }`}
            />
          </>
        )}
        <div
          className={`absolute inset-y-3 left-0 w-1 rounded-r-full ${accentBarClass}`}
        />
        <div className="relative flex items-center gap-3 mb-2">
          {Icon && (
            <span
              className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${iconClass}`}
            >
              <Icon className="h-5 w-5" />
            </span>
          )}
          <div>
            <div className={`text-sm font-semibold ${titleClass}`}>
              {title}
            </div>
            {subtitle && (
              <div className="text-xs opacity-70">{subtitle}</div>
            )}
          </div>
        </div>
        <div className="relative text-xs opacity-70">
          {t('metric_card.no_data')}
        </div>
      </div>
    );
  }

  const getZone = (value) => {
    if (!thresholds) return "neutral";
    const { low, high } = thresholds;
    if (value <= low) return "low";
    if (value >= high) return "high";
    return "mid";
  };

const ZONE =
  direction === "higherIsBetter"
    ? {
        low:  { bar: "bg-red-500",     tint: "bg-red-500/20",     label: t('metric_card.zone_low') },
        mid:  { bar: "bg-amber-500",   tint: "bg-amber-500/20",   label: t('metric_card.zone_medium') },
        high: { bar: "bg-emerald-500", tint: "bg-emerald-500/20", label: t('metric_card.zone_high') },
      }
    : {
        low:  { bar: "bg-blue-500",    tint: "bg-blue-500/20",    label: t('metric_card.zone_undertrained') },
        mid:  { bar: "bg-emerald-500", tint: "bg-emerald-500/20", label: t('metric_card.zone_optimal') },
        high: { bar: "bg-red-500",     tint: "bg-red-500/20",     label: t('metric_card.zone_atrisk') },
      }

  const {
    topVisible,
    lowestVisible: flopVisible,
    hasHiddenEntries,
  } = splitRankingHalves(entries, { expanded, collapsedLimit: 3 });

  const valuesArr = entries.map((e) => e.value);
  const vMin = min ?? Math.min(...valuesArr);
  const vMax = max ?? Math.max(...valuesArr);
  const span = vMax - vMin || 1;

  const valueToPercent = (v) => {
    const pct = ((v - vMin) / span) * 100;
    return Math.min(100, Math.max(4, pct));
  };

  const norm = (v) => {
    const x = ((v - vMin) / span) * 100;
    return Math.min(100, Math.max(0, x));
  };

  const lowPos = thresholds ? norm(thresholds.low) : null;
  const highPos = thresholds ? norm(thresholds.high) : null;

  const trackBg = isTrainabilityVariant
    ? isDarkMode
      ? "bg-slate-700/80"
      : "bg-slate-300/70"
    : isDarkMode
      ? "bg-gray-800"
      : "bg-gray-200";
  const trackHeightClass = isTrainabilityVariant ? "h-2" : "h-1.5";
  const textSoft = isDarkMode ? "text-gray-300" : "text-gray-600";

  const renderRow = (entry, index, kind) => {
    const { player, value } = entry;
    const initials = `${player.first_name?.[0] ?? ""}${player.last_name?.[0] ?? ""}`;
    const zone = getZone(value);
    const barColor = hideZoneBackgrounds
      ? (isDarkMode ? "bg-gray-400" : "bg-gray-500")
      : (ZONE[zone]?.bar ?? "bg-emerald-500");
    const customBarStyle = typeof getBarStyle === "function" ? getBarStyle(player, value, zone) : null;
    const muscleInfo = renderExtra ? renderExtra(player) : null;
    const delta = getDelta ? getDelta(player) : null;

    const clickable = !!onPlayerClick;

    return (
        <div
        key={`${kind}-${player.id}-${index}`}
        onClick={() => clickable && onPlayerClick(player)}
        className={`flex flex-col gap-1.5 rounded-xl px-2 py-1
            ${clickable ? "cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" : ""}`}
        >
        <div className="flex items-center gap-2">
            {/* Avatar */}
            <div className="h-8 w-8 rounded-full overflow-hidden bg-gradient-to-br from-indigo-500 to-sky-500 flex items-center justify-center text-xs font-semibold text-white">
            {player.profile_picture ? (
                <img
                src={`data:image/jpeg;base64,${player.profile_picture}`}
                alt={player.first_name + " " + player.last_name}
                className="h-full w-full object-cover"
                />
            ) : (
                initials
            )}
            </div>

            {/* Nom / valeur / delta / date */}
            <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
                <div className="truncate text-xs font-medium">
                {player.first_name} {player.last_name}
                </div>
                <div className="flex flex-col items-end gap-0.5">
                <div className="flex items-center gap-1">
                    <span className="text-[11px] tabular-nums opacity-80">
                    {formatValue(value)}
                    </span>

                    {Number.isFinite(delta) && (
                    <DeltaChip
                        delta={delta}
                        isPercent={deltaIsPercent}
                        unit={deltaUnit}
                        decimals={deltaDecimals}
                        label={deltaLabel}
                        invertTrendColors={invertDeltaColors}
                    />
                    )}
                </div>

                <div className="text-[10px] opacity-60">
                    {player.timeinfo_date
                    ? `${t('metrics.last_sim')}: ${player.timeinfo_date}`
                    : `${t('metrics.last_sim')}: ${t('common.na')}`}
                </div>
                </div>
            </div>

            {/* Slider */}
            <div className={`mt-1 ${trackHeightClass} w-full rounded-full ${trackBg} relative overflow-hidden`}>
                {thresholds && !hideZoneBackgrounds && (
                <>
                    <div className={`absolute inset-y-0 left-0 ${ZONE.low.tint}`} style={{ width: `${lowPos}%` }} />
                    <div className={`absolute inset-y-0 ${ZONE.mid.tint}`} style={{ left: `${lowPos}%`, width: `${Math.max(0, highPos - lowPos)}%` }} />
                    <div className={`absolute inset-y-0 ${ZONE.high.tint}`} style={{ left: `${highPos}%`, width: `${100 - highPos}%` }} />

                    <div className="absolute inset-y-0 w-[2px] bg-white/60" style={{ left: `${lowPos}%` }} />
                    <div className="absolute inset-y-0 w-[2px] bg-white/60" style={{ left: `${highPos}%` }} />
                </>
                )}

                {Array.isArray(referenceLines) && referenceLines.map((ref, refIndex) => {
                  const refValue = Number(ref?.value);
                  if (!Number.isFinite(refValue)) return null;
                  const refPos = norm(refValue);
                  return (
                    <div
                      key={`ref-${refIndex}`}
                      className="absolute inset-y-0 z-[1] w-[2px] bg-gray-500/70 dark:bg-gray-300/80"
                      style={{ left: `${refPos}%` }}
                      title={ref?.label ?? String(refValue)}
                    />
                  );
                })}

                <div
                className={`relative h-full rounded-full ${isTrainabilityVariant ? "shadow-[inset_0_1px_1px_rgba(255,255,255,0.35)]" : ""} ${customBarStyle ? "" : barColor}`}
                  style={{
                      width: `${valueToPercent(value)}%`,
                      ...(customBarStyle || {}),
                    }}
                />
            </div>
            </div>
        </div>

        {muscleInfo && (
            <div className={`pl-10 text-[11px] leading-snug ${textSoft}`}>
            {muscleInfo}
            </div>
        )}
        </div>
    );
    };

  return (
    <div className={cardClass}>
      {isTrainabilityVariant && (
        <>
          <div className="pointer-events-none absolute inset-0" style={trainabilityTextureStyle} />
          <div
            className={`pointer-events-none absolute inset-x-6 top-0 h-px ${
              isDarkMode
                ? "bg-gradient-to-r from-transparent via-white/35 to-transparent"
                : "bg-gradient-to-r from-transparent via-slate-400/45 to-transparent"
            }`}
          />
        </>
      )}
      {/* Barre de couleur latérale */}
      <div
        className={`absolute inset-y-3 left-0 w-1 rounded-r-full ${accentBarClass}`}
      />

      {/* Header */}
      <div className="relative flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          {Icon && (
            <span
              className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${iconClass}`}
            >
              <Icon className="h-5 w-5" />
            </span>
          )}
          <div>
            <div className={`text-sm font-semibold ${titleClass}`}>
              {title}
            </div>
            {subtitle && (
              <div className={`text-xs ${textSoft}`}>{subtitle}</div>
            )}
          </div>
        </div>

        {/* Badge + bouton collapse */}
        <div className="flex items-center gap-2">
          <span
            className={`hidden md:inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${badgeClass}`}
          >
           {t('metric_card.team_metric')}
          </span>
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            title={collapsed ? "Expand" : "Collapse"}
          >
            {collapsed ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronUp className="h-3 w-3" />
            )}
          </button>
        </div>
      </div>

      {/* Si replié → on cache tout le corps */}
      {collapsed ? null : (
        <div className="relative">
          {/* Légende des zones ou des repères */}
          {thresholds && !hideZoneBackgrounds && (
            <div className="mb-3 flex flex-wrap gap-4 text-[10px] uppercase tracking-wide opacity-70">
              <div className="flex items-center gap-1">
                <span className={`h-2 w-2 rounded-full ${ZONE.low.bar}`} />
                <span>{ZONE.low.label}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className={`h-2 w-2 rounded-full ${ZONE.mid.bar}`} />
                <span>{ZONE.mid.label}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className={`h-2 w-2 rounded-full ${ZONE.high.bar}`} />
                <span>{ZONE.high.label}</span>
              </div>
            </div>
          )}

          {hideZoneBackgrounds && Array.isArray(referenceLines) && referenceLines.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-4 text-[10px] uppercase tracking-wide opacity-70">
              {referenceLines.map((ref, refIndex) => (
                <div key={`legend-ref-${refIndex}`} className="flex items-center gap-1.5">
                  <span className="h-3 w-px bg-gray-500 dark:bg-gray-300" />
                  <span>{ref?.label ?? ref?.value}</span>
                </div>
              ))}
            </div>
          )}

          {/* Corps */}
          <div className="mt-2 flex flex-col md:flex-row gap-6">
            {/* TOP */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] uppercase tracking-wide opacity-70">
                  {t('metric_card.top_values')}
                </span>
              </div>
              <div className="space-y-3">
                {topVisible.map((entry, idx) => renderRow(entry, idx, "top"))}
              </div>
              {/* {!expanded && thresholds && extraTopCount > 0 && (
                <div className="mt-3 flex justify-center">
                  <span className="text-[11px] px-3 py-1 rounded-full bg-red-500/10 text-red-700 dark:text-red-300 font-semibold">
                    +{extraTopCount} additional at-risk player(s)
                  </span>
                </div>
              )} */}
            </div>

            <div className="hidden md:block w-px bg-gray-200 dark:bg-gray-700" />

            {/* FLOP */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] uppercase tracking-wide opacity-70">
                  {t('metric_card.lowest_values')}
                </span>
              </div>
              <div className="space-y-3">
                {flopVisible.map((entry, idx) => renderRow(entry, idx, "flop"))}
              </div>
              {/* {/* {!expanded && thresholds && extraFlopCount > 0 && (
                <div className="mt-3 flex justify-center">
                  <span className="text-[11px] px-3 py-1 rounded-full bg-blue-500/10 text-blue-700 dark:text-blue-300 font-semibold">
                    +{extraFlopCount} under-trained player(s) to watch
                  </span>
                </div>
              )} */}
            </div>
          </div>

          {/* Toggle étendre / réduire le classement */}
          {hasHiddenEntries && (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className={`inline-flex items-center gap-1 text-[11px] font-medium ${
                  isDarkMode
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {expanded
                  ? t('metric_card.collapse')
                  : t('metric_card.expand')}
                {expanded ? (
                  <ChevronUp className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3" />
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MetricRankingCard;
